import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class RecognizingValidationsImagesService {

  constructor() { }

  // Analyze skin texture for anti-spoofing
  analyzeSkinTexture(imageData: ImageData, height:number, width:number): number {
    const grayScale = this.convertToGrayscale(imageData);
    const laplacian = this.applyLaplacian(grayScale, height, width);
    return this.calculateVariance(laplacian);
  }

  private convertToGrayscale(imageData: ImageData): Uint8Array {
    const gray = new Uint8Array(imageData.data.length / 4);
    for (let i = 0; i < imageData.data.length; i += 4) {
      const r = imageData.data[i];
      const g = imageData.data[i + 1];
      const b = imageData.data[i + 2];
      gray[i / 4] = 0.299 * r + 0.587 * g + 0.114 * b;
    }
    return gray;
  }

  private applyLaplacian(gray: Uint8Array, height:number, width:number): Int16Array {
    const kernel = [-1, -1, -1, -1, 8, -1, -1, -1, -1];
    const result = new Int16Array(gray.length);
    // Aplicar convolución (omitimos bordes por simplicidad)
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        let sum = 0;
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const idx = (y + ky) * width + (x + kx);
            sum += gray[idx] * kernel[(ky + 1) * 3 + (kx + 1)];
          }
        }
        result[y * width + x] = sum;
      }
    }
    return result;
  }

  private calculateVariance(data: Int16Array): number {
    const mean = data.reduce((s, v) => s + v, 0) / data.length;
    return data.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / data.length;
  }

  // Detect specular reflections
  detectReflections(imageData: ImageData, height: number, width: number): number {
    let reflectionScore = 0;
    for (let i = 0; i < imageData.data.length; i += 4) {
      const r = imageData.data[i];
      const g = imageData.data[i + 1];
      const b = imageData.data[i + 2];
      const intensity = (r + g + b) / 3; // Calculate intensity

      // Check for high intensity (potential reflection)
      if (intensity > 200) { // Adjust threshold as needed
        reflectionScore++;
      }
    }
    return reflectionScore / (imageData.data.length / 4); // Normalize score
  }

  computeEdgeHistogram(imageData: ImageData, width: number, height: number): number {
    const gray = this.convertToGrayscale(imageData);
  
    const sobelX = [ -1, 0, 1, -2, 0, 2, -1, 0, 1 ];
    const sobelY = [ -1, -2, -1, 0, 0, 0, 1, 2, 1 ];
  
    const edgeMagnitudes: number[] = [];
  
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        let gx = 0, gy = 0;
        let i = 0;
  
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const px = x + kx;
            const py = y + ky;
            const idx = py * width + px;
            const pixel = gray[idx];
  
            gx += pixel * sobelX[i];
            gy += pixel * sobelY[i];
            i++;
          }
        }
  
        const magnitude = Math.sqrt(gx * gx + gy * gy);
        edgeMagnitudes.push(magnitude);
      }
    }
  
    // Histograma: contamos cuántos valores están por encima de cierto umbral
    const threshold = 100; // puedes ajustar
    const strongEdges = edgeMagnitudes.filter(val => val > threshold).length;
  
    return strongEdges / edgeMagnitudes.length; // proporción de bordes fuertes
  }

  computeLocalContrast(gray: number[], width: number, height: number): number {
    let contrastSum = 0;
    let count = 0;
  
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = y * width + x;
        const center = gray[idx];
  
        // Comparar con vecinos
        let diffSum = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const neighbor = gray[(y + dy) * width + (x + dx)];
            diffSum += Math.abs(center - neighbor);
          }
        }
  
        contrastSum += diffSum / 8;
        count++;
      }
    }
  
    return contrastSum / count;
  }
  getCanvasImage(video:HTMLVideoElement){
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 720;
    const ctx = canvas.getContext('2d',{ willReadFrequently: true });
    if(ctx){
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    }
    return canvas;
  }

  
  getCanvasImageV2(video: HTMLVideoElement, targetSize: number = 416): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    
    // Mantener relación de aspecto original del video
    const aspectRatio = video.videoWidth / video.videoHeight;
    const [width, height] = aspectRatio >= 1 
        ? [targetSize, targetSize / aspectRatio] 
        : [targetSize * aspectRatio, targetSize];

    canvas.width = Math.floor(width);
    canvas.height = Math.floor(height);
    
    const ctx = canvas.getContext('2d', { 
        willReadFrequently: true,
        alpha: false // Mejor rendimiento si no necesitas transparencia
    });
    
    if (ctx) {
        // Escalado proporcional con suavizado controlado
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(
            video,
            0, 0, video.videoWidth, video.videoHeight, // Área fuente
            0, 0, canvas.width, canvas.height // Área destino
        );
    }
    
    return canvas;
  }
}
